import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import PanelTemplate from "../../templates/Panel";
import Box from "../../components/Box";
import Button from "../../components/Button";
import HelpTooltip from "../../components/HelpTooltip";
import Icon from "../../components/Icon";
import PageFooter from "../../components/PageFooter";
import PageHeader from "../../components/PageHeader";
import api from "../../services/api";
import { hasPlanFeature } from "../../services/auth";
import { renderError } from "../../helpers/errors";

import Preview, { contactIsMissing } from "./Preview";
import {
    AUDIENCES,
    COMPANY_FIELDS,
    DEFAULT_FIELDS,
    FIELDS,
    HIDDEN,
    STATES,
    audienceFromPersonTypes,
    hasPf,
    hasPj,
} from "./fields";

const sameFields = (a, b) => FIELDS.every(field => a[field.key] === b[field.key]);

export const BudgetForm = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [personTypes, setPersonTypes] = useState(['PJ', 'PF']);
    const [fields, setFields] = useState(DEFAULT_FIELDS);
    const [saved, setSaved] = useState(null);
    const [previewAs, setPreviewAs] = useState('PJ');

    useEffect(() => {
        getConfig();
    }, []);

    // Derivado, e não corrigido por efeito: um `useEffect` só acertaria o tipo
    // no quadro seguinte ao clique, e nesse quadro a prévia apareceria sem os
    // campos de empresa. `previewAs` guarda apenas a escolha de quem está
    // olhando, e vale só quando os dois públicos estão liberados.
    const effectivePreviewAs = hasPj(personTypes) && hasPf(personTypes)
        ? previewAs
        : (hasPj(personTypes) ? 'PJ' : 'PF');

    const getConfig = () => {
        setLoading(true);

        api.get(`/budget-form`).then(res => {
            const resource = res.data?.resource ?? {};
            applyConfig(resource);
            setSaved(resource);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const applyConfig = config => {
        setPersonTypes(config.person_types ?? ['PJ', 'PF']);
        setFields({ ...DEFAULT_FIELDS, ...(config.fields ?? {}) });
    }

    const audience = audienceFromPersonTypes(personTypes);
    const missingContact = contactIsMissing(fields);

    // Mesmo gate do backend (BudgetService::addBudgetSite): quem não tem a
    // feature nunca teve envio automático, então avisar que ele "não sai
    // sozinha" descreve uma perda que esse tenant não tem.
    const sendsAutomaticProposal = hasPlanFeature('automatic_proposal_send');

    const dirty = useMemo(() => {
        if (!saved) return false;
        return audienceFromPersonTypes(saved.person_types) !== audience
            || !sameFields(saved.fields ?? {}, fields);
    }, [saved, audience, fields]);

    /**
     * A escolha de público precisa valer nos dois sentidos.
     *
     * Escondemos empresa e CNPJ ao sair do PJ (o backend faz o mesmo em
     * BudgetFormConfig::normalize()), mas sem trazê-los de volta ao voltar para
     * o PJ o tenant escolhia "Só empresas" e a prévia não mostrava dado nenhum
     * de empresa — a tela contradizendo o card que ele acabou de clicar.
     */
    const changeAudience = key => {
        const next = AUDIENCES.find(item => item.key === key);
        if (!next) return;

        const allowsCompany = next.personTypes.includes('PJ');

        setPersonTypes(next.personTypes);

        setFields(prev => {
            if (!allowsCompany) {
                return { ...prev, trade_name: HIDDEN, cnpj: HIDDEN };
            }

            // Os dois escondidos ao voltar para um público com empresa só
            // acontece por causa da escolha anterior: um público com empresa
            // que não pede nada da empresa é o mesmo que não aceitar empresa.
            // Nesse caso os campos voltam ao default.
            if (!COMPANY_FIELDS.every(field => prev[field] === HIDDEN)) {
                return prev;
            }

            return {
                ...prev,
                trade_name: DEFAULT_FIELDS.trade_name,
                cnpj: DEFAULT_FIELDS.cnpj,
            };
        });
    }

    const changeField = (key, state) => setFields(prev => ({ ...prev, [key]: state }));

    const save = () => {
        if (missingContact) return;

        setSaving(true);

        api.put(`/budget-form`, { person_types: personTypes, fields }).then(res => {
            const resource = res.data?.resource ?? {};
            applyConfig(resource);
            setSaved(resource);
            toast.success('Formulário publicado no site');
        }).catch(error => {
            renderError(error);
        }).then(() => setSaving(false));
    }

    const discard = () => {
        if (!saved) return;
        applyConfig(saved);
    }

    const visibleFields = FIELDS.filter(field => !(COMPANY_FIELDS.includes(field.key) && !hasPj(personTypes)));

    return (
        <PanelTemplate>
            <PageHeader left={<h1>Formulário de orçamento</h1>} />

            <div className="budget-form-page">
                <div className="budget-form-controls">
                    <Box title={`Quem pode pedir orçamento`}>
                        <div className="audience-cards">
                            {AUDIENCES.map(item => (
                                <button
                                    key={item.key}
                                    type="button"
                                    className={`audience-card ${audience === item.key ? 'on' : ''}`}
                                    onClick={() => changeAudience(item.key)}
                                    aria-pressed={audience === item.key}
                                >
                                    <Icon name={item.icon} size={22} strokeWidth={1.75} />
                                    <strong>{item.title}</strong>
                                </button>
                            ))}
                        </div>
                    </Box>

                    <Box title={`Campos do formulário`}>
                        <div className="field-rows">
                            {visibleFields.map(field => (
                                <div
                                    key={field.key}
                                    className={`field-row ${fields[field.key] === HIDDEN ? 'off' : ''}`}
                                >
                                    <div className="field-text">
                                        <span className="field-name">
                                            {field.name}
                                            {field.locked &&
                                                <>
                                                    <span className="field-lock">
                                                        <Icon name="lock" size={11} strokeWidth={2.5} />
                                                        sempre
                                                    </span>
                                                    <HelpTooltip text={field.lockReason} size={13} />
                                                </>
                                            }
                                        </span>
                                        <span className="field-description">{field.description}</span>
                                    </div>

                                    <div className={`state-switch ${field.locked ? 'locked' : ''}`}>
                                        {STATES.map(state => (
                                            <button
                                                key={state.value}
                                                type="button"
                                                className={fields[field.key] === state.value ? 'on' : ''}
                                                disabled={field.locked}
                                                onClick={() => changeField(field.key, state.value)}
                                                aria-pressed={fields[field.key] === state.value}
                                            >
                                                {state.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {fields.customer_email === HIDDEN &&
                            <div className="field-note warn">
                                <Icon name="alert" size={16} strokeWidth={2} />
                                {sendsAutomaticProposal
                                    ? <span>
                                        <strong>Sem e-mail, a proposta não sai sozinha.</strong>
                                        O envio automático da proposta usa o e-mail. Sem ele, o orçamento
                                        cai na sua lista para você responder por telefone ou WhatsApp, e
                                        clientes que já compraram são reconhecidos pelo telefone.
                                    </span>
                                    : <span>
                                        <strong>Sem e-mail, o cliente é reconhecido pelo telefone.</strong>
                                        Quem já pediu orçamento antes só cai no mesmo cadastro se informar
                                        o mesmo telefone — um número diferente cria um cliente novo.
                                    </span>
                                }
                            </div>
                        }

                        {missingContact &&
                            <div className="field-note stop">
                                <Icon name="alert" size={16} strokeWidth={2} />
                                <span>
                                    <strong>Você precisa de um jeito de responder.</strong>
                                    Deixe telefone ou e-mail como obrigatório — senão chega pedido de
                                    orçamento sem nenhum contato do cliente.
                                </span>
                            </div>
                        }
                    </Box>
                </div>

                <div className="budget-form-side">
                    <Preview
                        personTypes={personTypes}
                        fields={fields}
                        previewAs={effectivePreviewAs}
                        onChangePreviewAs={setPreviewAs}
                    />
                </div>
            </div>

            <PageFooter spaceBetween>
                <span className="budget-form-status">
                    {missingContact
                        ? 'Ajuste o contato para poder salvar.'
                        : dirty
                            ? 'Alterações não salvas.'
                            : 'Tudo salvo.'}
                </span>
                <div className="budget-form-actions">
                    <Button
                        type={`secondary`}
                        size={`small`}
                        text={`Descartar`}
                        action={discard}
                        disabled={!dirty || saving || loading}
                    />
                    <Button
                        type={`primary`}
                        size={`small`}
                        svg={`save-white`}
                        text={`Salvar alterações`}
                        action={save}
                        loading={saving || loading}
                        disabled={missingContact || !dirty}
                    />
                </div>
            </PageFooter>
        </PanelTemplate>
    );
}

export default BudgetForm;
